import styled from '@emotion/styled'
import { useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import { Link } from 'react-router'

import { CautionTriangle, CircleCheckFilled } from '../../../../assets/icons'
import { HelpIcon, TooltipText } from '../../../../components'
import { XSmallSecondaryButton, XSmallTertiaryButton } from '../../../../components/Button'
import { accountTypeKeyToPlanTypeId, planTypeToAccountLabel } from '../../../../constants'
import { MOBILE } from '../../../../guided-toolbox/utils/breakpoints'
import { withRouterProps } from '../../../../utils'
import { UPLOAD_STATUS, investmentsReviewPath } from '../../../../models/InvestmentUpload'
import UploadDropZone from './UploadDropZone'
import UploadSpinner from './UploadSpinner'
import { UPLOAD_COPY, validateStatementFiles } from './uploadCopy'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 199;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
`

const Dialog = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 600px;
  max-height: calc(100vh - 3rem);
  margin: 1.5rem;
  padding: 20px;
  overflow-y: auto;
  border: 1px solid #9eacb0;
  border-radius: 10px;
  background-color: #ffffff;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
`

const CloseIcon = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 10px;
  border: none;
  background: none;
  color: ${p => p.theme.lightestGray ?? '#7a8e96'};
  font-size: 34px;
  line-height: 24px;
  cursor: pointer;
`

/* The help icon sits beside the heading rather than inside it: a heading takes phrasing
   content, and HelpIcon renders a block element. */
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`

const Title = styled.h2`
  margin: 0;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-size: 32px;
  font-weight: 300;
  line-height: 40px;

  ${MOBILE} {
    font-size: 26px;
    line-height: 34px;
  }
`

const Disclaimer = styled.p`
  margin: 10px 0 20px;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-size: 18px;
  font-weight: 300;
  line-height: 24px;

  a {
    color: ${p => p.theme.primaryColor ?? '#038ab7'};
    text-decoration: underline;
  }
`

/** Status panel occupying the drop zone once a file is in flight or finished. */
const StatusPanel = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 203px;
  padding: 20px 24px;
  border: 2px dashed ${p => p.theme.donutPlanColor ?? '#4fadcc'};
  border-radius: 10px;
  background: ${p => p.theme.cardHoverBgColor ?? '#f5fafc'};
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-size: 16px;
  text-align: center;
`

const FileName = styled.div`
  font-size: 16px;
  font-weight: 700;
`

const ErrorHeading = styled.div`
  color: #ce0b24;
  font-size: 16px;
`

const Footer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`

const GhostButton = styled.button`
  border: none;
  background: none;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-family: inherit;
  font-size: 16px;
  text-decoration: underline;
  cursor: pointer;
`

const RetryPill = styled(XSmallTertiaryButton)`
  min-width: 180px;
`

const ClosePill = props => <XSmallSecondaryButton {...props} type='button' />

const statementTitle = accountType => {
  const label = planTypeToAccountLabel[accountTypeKeyToPlanTypeId[accountType]]
  return label ? `Upload your ${label} statement` : 'Upload your statement'
}

/**
 * Desktop statement upload modal.
 *
 * Rendered once for the accounts subtree; the account it targets comes from
 * `store.uploadModal`. The user may close it mid-upload — processing continues in the
 * background and finishes with a toast.
 */
const UploadStatementModal = props => {
  const { store } = props
  const { uploadModal } = store
  const accountId = uploadModal?.accountId

  // Own the registration here so it cannot be left behind if the screen hosting this modal
  // goes away mid-upload. A stale registration makes the background completion think the user
  // is still watching, so it skips the refresh, the review and the toast entirely.
  useEffect(() => {
    if (!accountId) return

    store.registerUploadView(accountId)

    return () => store.unregisterUploadView(accountId)
  }, [accountId, store])

  if (!uploadModal) return null

  const { type, accountType } = uploadModal
  const upload = store.getUpload(accountId)
  const status = upload?.status ?? UPLOAD_STATUS.idle
  const fileName = upload?.fileName ?? ''
  const isUploading = store.isAccountUploading(accountId)
  const reviewPath = investmentsReviewPath({ type, accountType, accountId })

  const handleFilesSelected = files => {
    const error = validateStatementFiles(files)

    if (error) {
      store.showToast({ severity: 'error', message: error })
      return
    }

    store.startStatementUpload({ type, accountType, accountId, file: files[0] })
  }

  /** Dismisses the modal and forgets the upload — only valid before one is in flight. */
  const handleClose = () => {
    store.clearUpload(accountId)
    store.closeUploadModal()
  }

  /** Leaves an in-flight upload running and shows its progress on the account summary. */
  const handleCloseWhileUploading = () => {
    store.closeUploadModal()
    props.navigate(`/accounts/${type}/${accountType}/${accountId}`)
  }

  /**
   * Hands the processed document to the review screen.
   *
   * The refresh is awaited before navigating so the review screen already has the extracted
   * investments when it mounts.
   */
  const handleReview = async () => {
    store.setInvestmentsReview(accountId, true, reviewPath)
    store.closeUploadModal()
    await store.refreshAccountAfterUpload(accountId)
    store.clearUpload(accountId)
    props.navigate(reviewPath)
  }

  /** Clears the failed attempt so the drop zone comes back for another file. */
  const handleRetry = () => store.clearUpload(accountId)

  const dismiss =
    status === UPLOAD_STATUS.success
      ? handleReview
      : isUploading
        ? handleCloseWhileUploading
        : handleClose

  return (
    <Overlay role='presentation'>
      <Dialog role='dialog' aria-modal='true' aria-labelledby='upload-statement-title'>
        <CloseIcon type='button' aria-label='Close' onClick={dismiss}>
          &times;
        </CloseIcon>

        <TitleRow>
          <Title id='upload-statement-title'>{statementTitle(accountType)}</Title>
          {/* SVGWrapper sizes the glyph from a named token, so this is the lever rather than CSS.
              'mediumLarge' is 20px, between 'medium' at 16 and 'large' at 24. */}
          <HelpIcon size='mediumLarge' tooltip={TooltipText.uploadRequirements()} />
        </TitleRow>

        <Disclaimer>
          {UPLOAD_COPY.disclaimer} <Link to='/ai-disclaimer'>{UPLOAD_COPY.disclaimerLink}</Link>
        </Disclaimer>

        {isUploading ? (
          <StatusPanel>
            <div>{UPLOAD_COPY.processingLead}</div>
            <UploadSpinner />
            <div>{UPLOAD_COPY.processingNote}</div>
          </StatusPanel>
        ) : status === UPLOAD_STATUS.success ? (
          <StatusPanel>
            <FileName>{fileName}</FileName>
            <CircleCheckFilled />
            <div>{UPLOAD_COPY.successHeading}</div>
          </StatusPanel>
        ) : status === UPLOAD_STATUS.error ? (
          <StatusPanel>
            <FileName>{fileName}</FileName>
            <CautionTriangle />
            <ErrorHeading>{UPLOAD_COPY.errorHeading}</ErrorHeading>
            <div>{UPLOAD_COPY.errorBody}</div>
          </StatusPanel>
        ) : status === UPLOAD_STATUS.backendError ? (
          <StatusPanel>
            <FileName>{fileName}</FileName>
            <CautionTriangle />
            <ErrorHeading>{UPLOAD_COPY.backendErrorHeading}</ErrorHeading>
            <div>{UPLOAD_COPY.backendErrorBody}</div>
          </StatusPanel>
        ) : (
          <UploadDropZone onFilesSelected={handleFilesSelected} />
        )}

        <Footer>
          {isUploading ? (
            <ClosePill onClick={handleCloseWhileUploading}>Close</ClosePill>
          ) : status === UPLOAD_STATUS.success ? (
            <ClosePill onClick={handleReview}>Close</ClosePill>
          ) : status === UPLOAD_STATUS.error ? (
            <RetryPill type='button' onClick={handleRetry}>
              Pick a different file
            </RetryPill>
          ) : status === UPLOAD_STATUS.backendError ? (
            <ClosePill onClick={handleClose}>Close</ClosePill>
          ) : (
            <GhostButton type='button' onClick={handleClose}>
              Add investments another way
            </GhostButton>
          )}
        </Footer>
      </Dialog>
    </Overlay>
  )
}

export default withRouterProps(inject('store')(observer(UploadStatementModal)))
